/**
 * booking-html-generator.js
 * Module: Invoice
 *
 * Builds the printable/PDF-able booking invoice: a self-contained HTML
 * document (inline <style>, no external assets) rendered off-screen and
 * captured with html2canvas + jsPDF by the caller (see
 * components/BookingHistory/Newaircargodetail.jsx). Exposes two functions —
 * `formatBookingDataForHTML` maps a raw booking record onto the flat prop
 * shape `generateBookingHTML` expects, keeping the HTML template free of
 * knowledge about the booking API's nested shape.
 *
 * Author: FreightSee frontend team
 * Date: 2026-07-06
 */

/**
 * Renders the full invoice as an HTML string.
 *
 * @param {Object} data - flattened booking fields, see the destructured
 *   defaults below for the full shape (header/basic-info/rate-details/GST/
 *   admin/ODCC/flight/shipment fields). Callers should build this via
 *   `formatBookingDataForHTML` rather than constructing it by hand.
 * @returns {string} a complete `<!DOCTYPE html>` document as a string
 */
export const generateBookingHTML = ({
  // Header data
  carrierName = "N/A",
  awbNumber = "N/A",
  origin = "N/A",
  destination = "N/A",
  volume = "0",
  volumeUnit = "",
  weight = "0",
  weightUnit = "",
  bookingStatus = "N/A",
  // Basic booking information
  chargeableWeight = "N/A",
  commodity = "N/A",
  departureDate,
  arrivalDate,
  bookingDate,
  // Rate details
  ratePerKg = 0,
  totalFreight = 0,
  pickupCharges = 0,
  deliveryCharges = 0,
  originCharges = 0,
  destinationCharges = 0,
  // GST breakdown — only rendered when gstAmount > 0 (older/GST-exempt bookings unaffected)
  taxableAmount = 0,
  gstAmount = 0,
  netAmount = 0,
  // Admin/KAM fields
  buyRate = 0,
  markupPercentage = 0,
  markupValue = 0,
  showAdminFields = false,
  // ODCC details
  minMarkup = 0,
  customerCategory = "N/A",
  odccStrategy = "N/A",
  odccPercentage = 0,
  // Flight details
  lat = "N/A",
  tao = "N/A",
  flightItineraries = [],
  // Shipment dimensions
  shipmentDimensions = [],
  // Currency
  currencySymbol = "₹",
}) => {
  /**
   * Formats a number as an INR currency string (e.g. "₹1,23,456.00").
   * @param {number|string} amount - raw amount, or "N/A"/falsy for a placeholder
   * @returns {string} the formatted amount, "N/A", or "₹0.00" for a missing amount
   */
  const formatCurrency = (amount) => {
    if(amount==="N/A") return "N/A"
    if (!amount) return `${currencySymbol}0.00`
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  /**
   * Formats a date string as DD/MM/YYYY for the invoice.
   * @param {string} dateString - any Date-parseable date string, or "N/A"
   * @returns {string} the formatted date, or "N/A" if missing/invalid
   */
  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  /**
   * Builds the <tr> rows for the Flight Details table, one per itinerary leg.
   * @returns {string} the table body HTML, or a single "N/A" row when there are no legs
   */
  const generateFlightRows = () => {
    if (!flightItineraries || flightItineraries.length === 0) {
      return `
        <tr>
          <td>1</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
        </tr>
      `
    }

    return flightItineraries
      .map(
        (flight, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${flight.carrierNumber || "N/A"}</td>
        <td>${formatDate(flight.scheduledArrivalDate)}</td>
        <td>${formatDate(flight.scheduledDepartureDate)}</td>
        <td>${flight.segmentOfDeparture || "N/A"}</td>
        <td>${flight.segmentOfArrival || "N/A"}</td>
        <td>${flight.carrierAircraftType || "N/A"}</td>
      </tr>
    `,
      )
      .join("")
  }

  /**
   * Builds the <tr> rows for the Shipment Dimensions table, one per package.
   * @returns {string} the table body HTML, or a single "N/A" row when there are no packages
   */
  const generateShipmentRows = () => {
    if (!shipmentDimensions || shipmentDimensions.length === 0) {
      return `
        <tr>
          <td>1</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
          <td>N/A</td>
        </tr>
      `
    }

    return shipmentDimensions
      .map(
        (shipment, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${shipment.numberOfPieces || "N/A"}</td>
        <td>${shipment.length || "N/A"}</td>
        <td>${shipment.width || "N/A"}</td>
        <td>${shipment.height || "N/A"}</td>
        <td>${shipment.dimensionsUnit || "N/A"}</td>
        <td>${shipment.weightPerPiece || "N/A"}</td>
        <td>${shipment.weightUnit || "N/A"}</td>
        <td>${shipment.turnable ? "Yes" : "No"}</td>
        <td>${shipment.stackable ? "Yes" : "No"}</td>
      </tr>
    `,
      )
      .join("")
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Booking Details - ${awbNumber}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background-color: #ffffff;
            color: #333;
            line-height: 1.6;
            font-size: 14px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }
        
        .card {
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            margin-bottom: 20px;
            overflow: hidden;
            page-break-inside: avoid;
        }
        
        .header-section {
            background: #FFE6CC;
            padding: 20px;
            display: flex;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;
            page-break-inside: avoid;
        }
        
        .airline-icon {
            width: 56px;
            height: 56px;
            background: white;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: #FF6B35;
            font-size: 24px;
        }
        
        .booking-summary {
            flex: 1;
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;
            word-wrap: break-word;
        }
        
        .section {
            padding: 32px;
            page-break-inside: avoid;
        }
        
        .section-title {
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 24px;
            color: #2c3e50;
            border-bottom: 2px solid #e9ecef;
            padding-bottom: 8px;
        }
        
        .grid {
            display: grid;
            gap: 24px;
            margin-bottom: 32px;
        }
        
        .grid-3 {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }
        
        .grid-4 {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        }
        
        .field-group {
            display: flex;
            flex-direction: column;
            page-break-inside: avoid;
        }
        
        .field-label {
            font-weight: 500;
            margin-bottom: 8px;
            color: #495057;
            font-size: 14px;
        }
        
        .field-value {
            padding: 12px;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            background-color: #f8f9fa;
            font-size: 14px;
            min-height: 40px;
            display: flex;
            align-items: center;
            word-wrap: break-word;
        }
        
        .table-container {
            margin: 24px 0;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
            page-break-inside: avoid;
        }
        
        .table-header {
            background: #f8f9fa;
            padding: 16px;
            font-size: 18px;
            font-weight: 600;
            border-bottom: 1px solid #dee2e6;
        }
        
        .table-fields {
            padding: 16px;
            display: flex;
            gap: 32px;
            flex-wrap: wrap;
            align-items: center;
            background: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
        }
        
        .table-field {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .table-field label {
            font-weight: 600;
            min-width: 40px;
        }
        
        .table-field input {
            padding: 8px 12px;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            background: white;
            width: 200px;
            font-size: 14px;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            page-break-inside: auto;
        }
        
        th, td {
            padding: 12px 8px;
            text-align: left;
            border: 1px solid #dee2e6;
            font-size: 12px;
            word-wrap: break-word;
            max-width: 120px;
        }
        
        th {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #495057;
            page-break-after: avoid;
        }
        
        tr {
            page-break-inside: avoid;
        }
        
        tr:nth-child(even) {
            background-color: #f8f9fa;
        }
        
        tr:hover {
            background-color: #e9ecef;
        }
        
        /* Page break handling */
        .page-break-avoid {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
        }
        
        .page-break-before {
            page-break-before: always !important;
            break-before: page !important;
        }
        
        .page-break-after {
            page-break-after: always !important;
            break-after: page !important;
        }
        
        /* Print styles */
        @media print {
            body {
                background: white;
                font-size: 12px;
            }
            
            .container {
                max-width: none;
                padding: 10px;
            }
            
            .card {
                box-shadow: none;
                border: 1px solid #dee2e6;
                margin-bottom: 10px;
            }
            
            .section {
                padding: 20px;
            }
            
            .page-break-avoid {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            
            .grid-3, .grid-4 {
                grid-template-columns: repeat(3, 1fr);
            }
            
            th, td {
                font-size: 10px;
                padding: 8px 4px;
            }
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
            .header-section {
                flex-direction: column;
                text-align: center;
            }
            
            .grid-3, .grid-4 {
                grid-template-columns: 1fr;
            }
            
            .table-fields {
                flex-direction: column;
                align-items: stretch;
            }
            
            .table-field {
                justify-content: space-between;
            }
            
            .table-field input {
                width: auto;
                flex: 1;
            }
            
            th, td {
                font-size: 11px;
                padding: 8px 4px;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="card" id="pdf-wrapper">
            <!-- Header Section -->
            <div class="header-section page-break-avoid">
                <div class="airline-icon">
                    ✈️
                </div>
                <div class="booking-summary">
                    ${carrierName} | ${awbNumber} | ${origin} - ${destination}, ${volume} ${volumeUnit}, ${weight} ${weightUnit}, ${bookingStatus}
                </div>
            </div>

            <!-- Basic Booking Information -->
            <div class="section page-break-avoid">
                <h2 class="section-title">Booking Details</h2>
                <div class="grid grid-3">
                    <div class="field-group">
                        <label class="field-label">Origin</label>
                        <div class="field-value">${origin}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Destination</label>
                        <div class="field-value">${destination}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Chargeable Weight</label>
                        <div class="field-value">${chargeableWeight}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Commodity</label>
                        <div class="field-value">${commodity}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Departure Date (Origin)</label>
                        <div class="field-value">${formatDate(departureDate)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Arrival Date (Destination)</label>
                        <div class="field-value">${formatDate(arrivalDate)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Current Status</label>
                        <div class="field-value">${bookingStatus}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">AWB Number</label>
                        <div class="field-value">${awbNumber}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Booking Date</label>
                        <div class="field-value">${formatDate(bookingDate)}</div>
                    </div>
                </div>
            </div>

            <!-- Rate Details Section -->
            <div class="section page-break-avoid">
                <h2 class="section-title">Rate Details</h2>
                <div class="grid grid-3">
                    <div class="field-group">
                        <label class="field-label">Air Freight Rate/kg</label>
                        <div class="field-value">${formatCurrency(ratePerKg)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Air Freight Rate/Total</label>
                        <div class="field-value">${formatCurrency(totalFreight)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Origin Transportation Charges</label>
                        <div class="field-value">${formatCurrency(pickupCharges)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Destination Delivery Charges</label>
                        <div class="field-value">${formatCurrency(deliveryCharges)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Other Origin Charges</label>
                        <div class="field-value">${formatCurrency(originCharges)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Other Destination Charges</label>
                        <div class="field-value">${formatCurrency(destinationCharges)}</div>
                    </div>
                    ${
                      showAdminFields
                        ? `
                    <div class="field-group">
                        <label class="field-label">Buy/kg</label>
                        <div class="field-value">${formatCurrency(buyRate)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Markup %</label>
                        <div class="field-value">${markupPercentage}%</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Markup value per/kg</label>
                        <div class="field-value">${formatCurrency(markupValue)}</div>
                    </div>
                    `
                        : ""
                    }
                </div>
            </div>

            ${
              gstAmount > 0
                ? `
            <!-- Amount Summary Section (GST breakdown) -->
            <div class="section page-break-avoid">
                <h2 class="section-title">Amount Summary</h2>
                <div class="grid grid-3">
                    <div class="field-group">
                        <label class="field-label">Taxable Amount</label>
                        <div class="field-value">${formatCurrency(taxableAmount)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">GST Amount (18%)</label>
                        <div class="field-value">${formatCurrency(gstAmount)}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Net Amount</label>
                        <div class="field-value" style="font-weight:700;">${formatCurrency(netAmount)}</div>
                    </div>
                </div>
            </div>
            `
                : ""
            }

            ${
              showAdminFields
                ? `
            <!-- ODCC Details Section -->
            <div class="section page-break-avoid">
                <h2 class="section-title">ODCC Details</h2>
                <div class="grid grid-4">
                    <div class="field-group">
                        <label class="field-label">Minimum Markup %</label>
                        <div class="field-value">${minMarkup}%</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">Customer Category</label>
                        <div class="field-value">${customerCategory}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">ODCC Strategy</label>
                        <div class="field-value">${odccStrategy}</div>
                    </div>
                    <div class="field-group">
                        <label class="field-label">ODCC %</label>
                        <div class="field-value">${odccPercentage}%</div>
                    </div>
                </div>
            </div>
            `
                : ""
            }

            <!-- Flight Details Section -->
            <div class="section page-break-avoid">
                <div class="table-container">
                    <div class="table-header">Flight Details</div>
                    <div class="table-fields">
                        <div class="table-field">
                            <label>LAT:</label>
                            <input type="text" value="${lat}" readonly>
                        </div>
                        <div class="table-field">
                            <label>TAO:</label>
                            <input type="text" value="${tao}" readonly>
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>S.NO</th>
                                <th>Carrier Number</th>
                                <th>Scheduled Arrival Date</th>
                                <th>Scheduled Departure Date</th>
                                <th>Segment of Departure</th>
                                <th>Segment of Arrival</th>
                                <th>Carrier Aircraft Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${generateFlightRows()}
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Shipment Dimensions Section -->
            <div class="section page-break-avoid">
                <div class="table-container">
                    <div class="table-header">Shipment Dimensions</div>
                    <table>
                        <thead>
                            <tr>
                                <th>S.NO</th>
                                <th>Number of Pieces</th>
                                <th>Length</th>
                                <th>Width</th>
                                <th>Height</th>
                                <th>Dimensions Unit</th>
                                <th>Weight per Piece</th>
                                <th>Weight Unit</th>
                                <th>Turnable</th>
                                <th>Stackable</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${generateShipmentRows()}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</body>
</html>`
}

// Helper function to extract data from your booking object and format it for the template
/**
 * Maps a raw booking record (as returned by the single_booking_data API)
 * onto the flat prop shape `generateBookingHTML` expects, so the HTML
 * template never has to know about the booking API's nested field names.
 *
 * @param {Object} bookingData - raw booking record: bookeditem, search_query_data, etc.
 * @param {"user"|"kam"|"air_admin"} [userRole="user"] - viewer's role; kam/air_admin
 *   get the extra buy-rate/markup/ODCC fields (`showAdminFields`)
 * @returns {Object} the flattened data object to pass into `generateBookingHTML`
 */
export const formatBookingDataForHTML = (bookingData, userRole = "user") => {
  const showAdminFields = userRole === "kam" || userRole === "air_admin"

  // GST breakdown — derived from the persisted chargesBreakDown, the same way
  // BookingDetailPage.jsx reconstructs it: GST is
  // just another category in the breakdown (added at checkout time), so there
  // is no separate GST field to read from the API.
  //
  // Why this doesn't affect older invoices: bookings made before GST was
  // introduced (or otherwise GST-exempt) have no "GST" category charge, so
  // gstAmount resolves to 0. generateBookingHTML only renders the Amount
  // Summary section when gstAmount > 0, so those invoices render exactly as
  // they did before this feature existed.
  const chargesBreakDown = bookingData?.bookeditem?.rateDetails?.chargesBreakDown || []
  const chargeAmt = (c) => Number(c?.Totalamount ?? c?.chargeAmount) || 0
  const hasGstLineItem = chargesBreakDown.some((c) => c.category === "GST")

  // Fallback GST — for bookings where hasBreakdown was false at checkout
  // (QR/direct-airline rate, or an empty chargesBreakDown), GST was still
  // calculated but never appended as its own chargesBreakDown line item —
  // see NewBookingFlowContent.jsx. It IS persisted as a plain
  // rateDetails.gstAmount field though, so fall back to that whenever no
  // "GST" category exists in the breakdown. Bookings that predate this GST
  // feature simply have no gstAmount field, so this fallback resolves to 0
  // and their invoice renders exactly as before.
  const gstAmount = hasGstLineItem
    ? chargesBreakDown.filter((c) => c.category === "GST").reduce((s, c) => s + chargeAmt(c), 0)
    : Number(bookingData?.bookeditem?.rateDetails?.gstAmount) || 0

  // Net Amount = every persisted charge, GST included; falls back to the
  // backend's stored totalAmount (already GST-inclusive) when there's no
  // breakdown at all. When there IS a breakdown but no GST line item, the
  // fallback gstAmount above is added on top since it isn't in the sum yet.
  const netAmount = chargesBreakDown.length
    ? chargesBreakDown.reduce((s, c) => s + chargeAmt(c), 0) + (hasGstLineItem ? 0 : gstAmount)
    : Number(bookingData?.bookeditem?.rateDetails?.totalAmount) || 0
  // Taxable Amount = Net Amount minus GST, i.e. what GST was calculated on.
  const taxableAmount = netAmount - gstAmount

  return {
    // Header data
    carrierName: bookingData?.bookeditem?.flightItineraries?.[0]?.carr_name + " Cargo" || "Airline Cargo",
    awbNumber: bookingData?.airWaybillNumber || "N/A",
    origin: bookingData?.bookeditem?.origin || "N/A",
    destination: bookingData?.bookeditem?.destination || "N/A",
    volume: bookingData?.search_query_data?.packagingDetails?.shipmentTotal?.totalvolume || "0",
    volumeUnit: bookingData?.search_query_data?.packagingDetails?.shipmentTotal?.unit || "",
    weight: bookingData?.search_query_data?.packagingDetails?.shipmentTotal?.totalweight || "0",
    weightUnit: bookingData?.search_query_data?.packagingDetails?.shipmentTotal?.weightunit || "",
    bookingStatus: bookingData?.booking_status || "N/A",

    // Basic booking information
    chargeableWeight: bookingData?.bookeditem?.rateDetails?.chargeableWeight || "N/A",
    commodity: bookingData?.search_query_data?.goodsDetails?.commonditytype?.commonditytype || "N/A",
    departureDate: bookingData?.bookeditem?.flightItineraries?.[0]?.scheduledDepartureDate,
    arrivalDate:
      bookingData?.bookeditem?.flightItineraries?.[bookingData?.bookeditem?.flightItineraries?.length - 1]
        ?.scheduledArrivalDate,
    bookingDate: bookingData?.booking_date,

    // Rate details
    ratePerKg: bookingData?.bookeditem?.rateDetails?.ratePerkilo || "N/A",
    totalFreight: bookingData?.bookeditem?.rateDetails?.totalAmount || "N/A",
    pickupCharges: bookingData?.viewBreakupCharge?.pickupCharges || "N/A",
    deliveryCharges: bookingData?.viewBreakupCharge?.deliveryCharges || "N/A",
    originCharges: bookingData?.viewBreakupCharge?.OriginCharges || "N/A",
    destinationCharges: bookingData?.viewBreakupCharge?.DestinationCharges || "N/A",

    // GST breakdown
    taxableAmount,
    gstAmount,
    netAmount,

    // Admin/KAM fields
    buyRate: bookingData?.buyRate?.rateperkillo || "N/A",
    markupPercentage: bookingData?.markupResult?.markup || "N/A",
    markupValue: bookingData?.markupResult?.markupRatePerKg || "N/A", // Calculate as needed
    showAdminFields,

    // ODCC details
    minMarkup: bookingData?.markupResult?.minimum_markup_percentage || "N/A",
    customerCategory: bookingData?.odcc_details?.customer_category || "N/A",
    odccStrategy: bookingData?.odcc_details?._id || "N/A",
    odccPercentage: bookingData?.odcc_details?.markup_percentage || "N/A",

    // Flight details
    lat: bookingData?.lat || "N/A",
    tao: bookingData?.tao || "N/A",
    flightItineraries: bookingData?.bookeditem?.flightItineraries || [],

    // Shipment dimensions
    shipmentDimensions: bookingData?.search_query_data?.airlinepayloadData?.shipmentDimensions || [],

    // Currency
    currencySymbol: "₹",
  }
}